import React, {useEffect, useRef, useState} from 'react';
import {
    LanguageSelectorButtonContainer,
    LanguageSelectorButtonLabelWrapper,
    LanguageSelectorButtonWrapper,
    LanguageSelectorContainer,
    LanguageSelectorDropdownList,
    LanguageSelectorDropdownListOption,
    LanguageSelectorDropdownListOptionContainer,
    LanguageSelectorFlagWrapper,
    LanguageSelectorLanguageNameWrapper
} from './LanguageSelector.styled';
import {languages} from "../../../config/languages";
import {useTranslation} from "react-i18next";
import {ExportDropdownChevron} from "../../ocr-batch/ExportDropdown/ExportDropdown.styled";

export type LanguageSelectorType = 'source' | 'target';
export type DropdownListJustify = 'left' | 'right';

interface LanguageSelectorProps {
  languageCode: string | null;
  languageSelectorType?: LanguageSelectorType;
  dropdownListJustify?: DropdownListJustify;
  onChange: (languageCode: string) => void;
  languageList: typeof languages;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = (props) => {
    const {t} = useTranslation();

    const [open, setOpen] = useState<boolean>(false);
    const selectorRef = useRef<HTMLDivElement>(null);

    const languageSelectorType = props.languageSelectorType;
    const dropdownListJustify = props.dropdownListJustify ?? 'left';

    useEffect(() => {
        const handleClickOutsideDropdownList = (event: MouseEvent) => {
            if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        if (open) {
            document.addEventListener('mousedown', handleClickOutsideDropdownList);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutsideDropdownList);
        };
    }, [open]);


    const handleSelectButtonOnClick = () => {
        setOpen(!open);
    }

    const handleLanguageChange = (languageCode: string) => {
        props.onChange(languageCode);
        setOpen(false);
    };

    return (
        <LanguageSelectorContainer ref={selectorRef}>
            <LanguageSelectorButtonContainer>
                <LanguageSelectorButtonLabelWrapper>
                    {languageSelectorType === 'source' ? t('textTranslator.sourceLanguageSelectorLabel') : ''}
                    {languageSelectorType === 'target' ? t('textTranslator.targetLanguageSelectorLabel') : ''}
                </LanguageSelectorButtonLabelWrapper>

                <LanguageSelectorButtonWrapper
                    onClick={handleSelectButtonOnClick}
                >
                    <LanguageSelectorFlagWrapper>
                        {props.languageList.find(language => language.code === props.languageCode)?.flagComponent}
                    </LanguageSelectorFlagWrapper>

                    <LanguageSelectorLanguageNameWrapper>
                        {props.languageList.find(language => language.code === props.languageCode)?.shortLabel}
                    </LanguageSelectorLanguageNameWrapper>

                    <ExportDropdownChevron $open={open} style={{ width: '7px', height: '7px'}} />
                </LanguageSelectorButtonWrapper>
            </LanguageSelectorButtonContainer>

            {open && <LanguageSelectorDropdownList $isOpen={open} $justify={dropdownListJustify}>
                {props.languageList.map(language => (
                    <LanguageSelectorDropdownListOption
                        key={language.code}
                        $isActive={props.languageCode === language.code}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleLanguageChange(language.code);
                        }}
                    >
                        <LanguageSelectorDropdownListOptionContainer>
                            <LanguageSelectorFlagWrapper>
                                {language.flagComponent}
                            </LanguageSelectorFlagWrapper>

                            {language.label}
                        </LanguageSelectorDropdownListOptionContainer>
                    </LanguageSelectorDropdownListOption>
                ))}
            </LanguageSelectorDropdownList>}
        </LanguageSelectorContainer>
    );
};

export default LanguageSelector;